import { Component, OnInit, Input, OnChanges, SimpleChanges,Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Options, ChangeContext } from '@angular-slider/ngx-slider';
import { Store } from '../../../../core/models';
import { AuthorizationAwareComponent } from '../../../../core/authorization-aware-component';
import { AppState } from '../../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import {  selectvolumeDetailState } from '../../../detail-state';
import { Subject, of, Observable } from 'rxjs';
import { filter, take, debounceTime, switchMap, catchError } from 'rxjs/operators';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { StoreRanking } from 'src/app/core/store/ranking/store-ranking';
import { ComparisonType } from 'src/app/core/store/ranking/comparison-type.enum';
import { StoreRankingService } from 'src/app/core/store/ranking/store-ranking.service';
import { StoreRankingRequest } from 'src/app/core/store/ranking/store-ranking-request';
import { KeyIndicatorType } from 'src/app/core/user-preferences/key-indicator-type.enum';
import { SalesSqftDisplayType } from 'src/app/core/user-preferences/sales-sqft-display-type.enum';
import { SalesVolumeDisplayType } from 'src/app/core/user-preferences/sales-volume-display-type.enum';
import { DetailState, selectDetailState } from '../../../detail-state';
import { FeatureCollection } from 'src/app/core/models';
import { selectUserState } from 'src/app/auth/user-state';
import { RankingFilters } from '../store-ranking-filters/store-ranking-filters.component';
import { RegionType } from 'src/app/core/location/region-type.enum';
import { StoreComparison } from 'src/app/core/store/ranking/store-comparison';
import { StoreComparisonService } from 'src/app/core/store/ranking/store-comparison.service';
import { DynamicPdfService } from 'src/app/core/service/dynamic-pdf.service';
import { DefinitionService } from 'src/app/core/definition/definition.service';
import { FitType } from 'src/app/core/store/fit/fit-type.enum';
import { formatAsCurrency } from 'src/app/core/util/string-utils';

// Internal component interfaces for scatter chart visualization
interface ScatterDataPoint {
  id: string;
  x: number;
  y: number;
  color: string;
  category: string;
  storeName: string;
  storeNumber: string;
  rank: number;
  fit: string;
  status: string;
  sqft: number;
  salesArea: number;
  percentile: number;
  value: number;
  bannerName?: string;
  isCurrentStore: boolean;
  rawValue: number;   // original ranking value before zoom mapping
  jitterX: number;    // stored jitter delta so recalculate doesn't re-jitter
  jitterY: number;
  visible: boolean;   // whether point is within current zoom range
}

interface YourStorePosition {
  x: number;
  y: number;
  name: string;
  rank: number;
  percentile: number;
  totalCount: number;
  fit: string;
  status: string;
  sqft: number;
  salesArea: number;
  value: number;
  storeNumber: string;
  rawValue: number;   // original ranking value for zoom recalculation
}

@Component({
  standalone: false,
  selector: 'mtn-store-ranking-scatter-chart',
  templateUrl: './store-ranking-scatter-chart.component.html',
  styleUrls: ['./store-ranking-scatter-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreRankingScatterChartComponent extends AuthorizationAwareComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() comparisonType: ComparisonType = ComparisonType.ALL;
  @Input() keyIndicator: KeyIndicatorType = KeyIndicatorType.SALES_VOLUME;
  @Input() isLoading: boolean = false;
  @Input() currentStoreUuid: string = '';
  @Input() filters: RankingFilters;

  @ViewChild('minInput') minInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('maxInput') maxInputRef: ElementRef<HTMLInputElement>;
  @Output() rankingDataUpdated = new EventEmitter<StoreRanking[]>();

  // Default filters
  private defaultFilters: RankingFilters = {
    storeType: 'All Grocery Stores',
    driveTime: 'Drive time',
    number: 7,
    comparisonType: ComparisonType.ALL
  };

  rankings: StoreRanking[] = [];
  dataPoints: ScatterDataPoint[] = [];
  visibleDataPoints: ScatterDataPoint[] = []; // filtered list for template rendering
  yourStorePosition: YourStorePosition = { x: 50, y: 50, name: 'Your Store', rank: 0, percentile: 0, totalCount: 0, fit: 'A', status: 'Open', sqft: 0, salesArea: 0, value: 0, storeNumber: '0', rawValue: 0 };
  hoveredPoint: ScatterDataPoint | null = null;
  tooltipPosition = { x: 0, y: 0 };
  isTooltipFlipped = false;
  tooltipArrowOffset = 50; // percentage from left of tooltip box
  showYourStoreTooltip = false;
  yourStoreTooltipPosition = { x: 0, y: 0 };
  isYourStoreTooltipFlipped = false;
  yourStoreTooltipArrowOffset = 50; // percentage from left of tooltip box
  store: Store;
  volume: any;
  hasData: boolean = false;
  storeStatusDisplay = 'N/A';
  fitCategoryDisplay = 'N/A';
  estSalesVolume = 'Not Available';
  estSalesSqft = 'Not Available';
  pdfGenerating: boolean = false;
  marketArea: FeatureCollection;
  rankingsLoading: boolean = false;
  xAxisLabels: string[] = [];
  minValue: number = 0;
  maxValue: number = 0;
  zoomMin: number = 0;
  zoomMax: number = 0;
  isZoomActive: boolean = false;
  isYourStoreVisible: boolean = true;
  sliderOptions: Options = { floor: 0, ceil: 1, step: 1 };
  private sortedRawValues: number[] = [];
  private zoomInputDebounceTimer: any = null;
  private _fetchedForStoreUuid: string = null;
  private _fetchTrigger$ = new Subject<void>();

  // Color configuration based on comparison type
  private readonly COLOR_CONFIG = {
    yourStore: '#26C066',
    sameBanner: '#3E82DA',
    localCompetitor: '#F4742F',
    marketCompetitor: '#6C7380'
  };

  constructor(
    protected ngrxStore: NgrxStore<AppState>,
    private hitTrackingService: HitTrackingService,
    private storeComparisonService: StoreComparisonService,
    private storeRankingService: StoreRankingService,
    private cdr: ChangeDetectorRef,
    private dynamicPdfService: DynamicPdfService,
    private definitionService: DefinitionService
  ) {
    super(ngrxStore);
  }

  onAuthorizationInit(): void {
    this.addSubscription(
      this._fetchTrigger$.pipe(
        debounceTime(50),
        switchMap(() => this.buildRankingObservable())
      ).subscribe({
        next: (rankings: StoreRanking[]) => {
          this.rankings = rankings || [];
          this.rankingsLoading = false;
          this.hasData = this.rankings.length > 0;
          this.processRankingsData();
          this.rankingDataUpdated.emit(this.rankings);
        },
        error: (error) => {
          console.error('rank-by-store API ERROR:', error);
          this.rankings = [];
          this.rankingsLoading = false;
          this.hasData = false;
          this.cdr.markForCheck();
        }
      })
    );
    this.loadStoreData();
  }

  onAuthorizationChange(): void {
  }

  ngOnInit(): void {
    super.ngOnInit(); // fires subscribeToAuthState → onAuthorizationInit → loadStoreData
  }

  ngAfterViewInit(): void {
    this.syncInputDisplays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const relevant = ['keyIndicator', 'comparisonType', 'filters'];
    const hasNonFirstChange = relevant.some(k => changes[k] && !changes[k].firstChange);
    if (hasNonFirstChange && this._fetchedForStoreUuid) {
      this.fetchStoreRankings();
    }
    if ((changes['rankings'] || changes['currentStoreUuid']) && this.rankings) {
      this.processRankingsData();
    }
  }

  private processRankingsData(): void {
    if (!this.rankings || this.rankings.length === 0) {
      this.dataPoints = [];
      this.visibleDataPoints = [];
      this.hasData = false;
      return;
    }

    this.hasData = true;

    // Find min/max with a single loop (avoids stack overflow with spread on large arrays)
    let minVal = Infinity;
    let maxVal = -Infinity;
    for (let i = 0; i < this.rankings.length; i++) {
      const v = this.rankings[i].value;
      if (!isNaN(v)) {
        if (v < minVal) minVal = v;
        if (v > maxVal) maxVal = v;
      }
    }
    this.minValue = minVal;
    this.maxValue = maxVal;

    // If only one store, create artificial range for proper positioning
    if (this.maxValue - this.minValue === 0) {
      const padding = Math.max(this.minValue * 0.2, 1000);
      this.minValue = minVal - padding;
      this.maxValue = maxVal + padding;
    }

    // Initialise zoom to full range
    this.zoomMin = this.minValue;
    this.zoomMax = this.maxValue;
    this.isZoomActive = false;
    this.isYourStoreVisible = true;
    this.initSliderOptions();

    const valueRange = this.maxValue - this.minValue || 1;
    const storeUuid = this.currentStoreUuid || this.store?.uuid;
    const storeBannerUuid = this.store?.banner?.uuid;

    // Find current store ranking
    const currentStoreRanking = this.rankings.find(r => r.store?.uuid === storeUuid);

    // Single-pass: build points array, pre-allocate
    const filtered = this.rankings;
    const points: ScatterDataPoint[] = [];
    for (let i = 0; i < filtered.length; i++) {
      const ranking = filtered[i];
      if (ranking.store?.uuid === storeUuid) continue;

      const isSameBanner = ranking.store?.banner?.uuid === storeBannerUuid;
      const baseX = ((ranking.value - this.minValue) / valueRange) * 100;
      const baseY = this.mapRankToPosition(ranking.rank, ranking.totalCount);

      points.push({
        id: ranking.store?.uuid || `store-${i}`,
        x: baseX,
        y: baseY,
        rawValue: ranking.value,
        jitterX: 0,
        jitterY: 0,
        color: this.getPointColor(false, isSameBanner, ranking),
        category: this.getCategory(false, isSameBanner, ranking),
        storeName: ranking.store?.name || 'Unknown Store',
        storeNumber: ranking.store?.number,
        rank: ranking.rank,
        fit: ranking.store?.fit,
        status: ranking.store?.currentStatus?.type?.systemName,
        sqft: ranking.volume?.salesSqftByTotalArea,
        salesArea: ranking.volume?.salesArea,
        percentile: ranking.percentile,
        value: ranking.volume?.total,
        bannerName: ranking.store?.banner?.name,
        isCurrentStore: false,
        visible: true
      });
    }

    // Sort by x for bucket-based jitter
    points.sort((a, b) => a.x - b.x);

    // O(n) bucket-based density jitter (replaces O(n²) filter scan)
    const bucketSize = 5; // percentage width of each bucket
    const bucketCounts = new Uint16Array(Math.ceil(100 / bucketSize) + 1);
    for (let i = 0; i < points.length; i++) {
      const bucketIdx = Math.min(bucketCounts.length - 1, (points[i].x / bucketSize) | 0);
      bucketCounts[bucketIdx]++;
    }
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const bucketIdx = Math.min(bucketCounts.length - 1, (p.x / bucketSize) | 0);
      const density = bucketCounts[bucketIdx];
      const jitterMul = Math.min(density / 10, 1);
      const jx = (Math.random() - 0.5) * 8 * jitterMul;
      const jy = (Math.random() - 0.5) * 6 * jitterMul;
      p.jitterX = jx;
      p.jitterY = jy;
      p.x = Math.min(99, Math.max(1, p.x + jx));
      p.y = Math.min(95, Math.max(5, p.y + jy));
    }

    this.dataPoints = points;

    // Set your store position
    if (currentStoreRanking) {
      this.yourStorePosition = {
        x: ((currentStoreRanking.value - this.minValue) / valueRange) * 100,
        y: this.mapRankToPosition(currentStoreRanking.rank, currentStoreRanking.totalCount),
        rawValue: currentStoreRanking.value,
        name: currentStoreRanking.store?.name || this.store?.name || 'Your Store',
        rank: currentStoreRanking.rank,
        percentile: currentStoreRanking.percentile,
        totalCount: currentStoreRanking.totalCount,
        fit: currentStoreRanking.store?.fit,
        status: currentStoreRanking.store?.currentStatus?.type?.systemName,
        sqft: currentStoreRanking.volume?.salesSqftByTotalArea,
        salesArea: currentStoreRanking.volume?.salesArea,
        value: currentStoreRanking.volume?.total,
        storeNumber: currentStoreRanking.store?.number,
      };
    }

    // Generate x-axis labels
    this.generateXAxisLabels();

    // Auto-zoom: min = data floor, max = your store + 50–100 stores beyond it
    this.sortedRawValues = points.map(p => p.rawValue).sort((a, b) => a - b);

    this.zoomMin = this.minValue; // always start from the data floor

    if (currentStoreRanking && points.length > 5) {
      const yourValue = currentStoreRanking.value;

      // Find the first index in sorted values that is >= your store's value
      let yourIdx = 0;
      for (let i = 0; i < this.sortedRawValues.length; i++) {
        if (this.sortedRawValues[i] >= yourValue) {
          yourIdx = i;
          break;
        }
        yourIdx = i; // fallback: your store is beyond all competitors
      }

      // Target: 75 stores after your store's position (midpoint of 50–100)
      const storesAfter = 75;
      const targetIdx = Math.min(this.sortedRawValues.length - 1, yourIdx + storesAfter);
      const targetValue = this.sortedRawValues[targetIdx];

      // Add 10% padding so the target store isn't flush at the edge
      const pad = (targetValue - this.minValue) * 0.10;
      let candidateMax = targetValue + pad;

      // GRO-722: the default window MUST always contain the current store. In dense
      // clusters (or when the store is a top performer) the competitor-derived target
      // can fall below the store's own value, pushing the green dot off the right edge.
      // Extend the max past the store's value (with padding) so it's always visible.
      // zoomMin is the global data floor, so the left edge already covers the store.
      const storePad = Math.max((this.maxValue - this.minValue) * 0.05, 1);
      if (yourValue + storePad > candidateMax) {
        candidateMax = yourValue + storePad;
      }

      // Only apply if this actually trims the range (i.e. your store isn't already near the top).
      // When it doesn't trim, the full range is shown — which also contains the current store —
      // so the store is visible either way.
      if (candidateMax < this.maxValue * 0.95) {
        this.zoomMax = Math.min(this.maxValue, candidateMax);
        this.isZoomActive = true;
      }
    }

    // Build initial visible list
    this.rebuildVisiblePoints();

    // Recalculate positions if zoom is active
    if (this.isZoomActive) {
      this.recalculatePositions();
    }

    // Tell Angular to re-render (OnPush — must be explicit)
    this.cdr.markForCheck();

    // Sync input fields after Angular has rendered
    setTimeout(() => {
      this.syncInputDisplays();
      this.cdr.markForCheck();
    });
  }

  private mapRankToPosition(rank: number, totalCount: number): number {
    if (!rank || !totalCount) return 50;
    // Map rank to y-axis: rank 1 at top (95%), highest rank at bottom (5%)
    const percentage = ((totalCount - rank + 1) / totalCount) * 90 + 5;
    return Math.min(95, Math.max(5, percentage));
  }

  private getPointColor(isCurrentStore: boolean, isSameBanner: boolean, ranking?: StoreRanking): string {
    if (isCurrentStore) {
      return this.COLOR_CONFIG.yourStore;
    }

    // Use competitionType from API response if available
    if (ranking?.competitionType) {
      switch (ranking.competitionType) {
        case 'SAME_BANNER':
          return this.COLOR_CONFIG.sameBanner;
        case 'LOCAL_COMPETITOR':
          return this.COLOR_CONFIG.localCompetitor;
        case 'MARKET_COMPETITOR':
          return this.COLOR_CONFIG.marketCompetitor;
        default:
          break;
      }
    }

    // Fallback to existing logic
    if (this.comparisonType === ComparisonType.BANNER || isSameBanner) {
      return this.COLOR_CONFIG.sameBanner;
    }
    // Alternate between local and market competitor colors
    return Math.random() > 0.5 ? this.COLOR_CONFIG.localCompetitor : this.COLOR_CONFIG.marketCompetitor;
  }

  private getCategory(isCurrentStore: boolean, isSameBanner: boolean, ranking?: StoreRanking): string {
    if (isCurrentStore) return 'current';

    // Use competitionType from API response if available
    if (ranking?.competitionType) {
      switch (ranking.competitionType) {
        case 'SAME_BANNER':
          return 'same-banner';
        case 'LOCAL_COMPETITOR':
          return 'local-competitor';
        case 'MARKET_COMPETITOR':
          return 'market-competitor';
        default:
          break;
      }
    }

    // Fallback to existing logic
    if (isSameBanner) return 'same-banner';
    return 'competitor';
  }

  trackById(_index: number, point: ScatterDataPoint): string {
    return point.id;
  }

  getXAxisTitle(): string {
    if (this.keyIndicator === 'SALES_SQFT') {
      return 'Sales per Sq Ft';
    }
    return 'Sales Volume';
  }

  getScatterPointStyle(point: ScatterDataPoint): { [key: string]: string } {
    return {
      left: `${point.x}%`,
      bottom: `${point.y}%`,
      backgroundColor: point.color
    };
  }

  getYourStoreStyle(): { [key: string]: string } {
    return {
      left: `${this.yourStorePosition.x}%`,
      bottom: `${this.yourStorePosition.y}%`
    };
  }

  getYourStoreLabel(): string {
    const storeName = this.store?.name || this.yourStorePosition.name;
    if (this.yourStorePosition.rank) {
      return `${storeName} (#${this.yourStorePosition.rank})`;
    }
    return storeName;
  }

  getYourStoreLabelClass(): string {
    const x = this.yourStorePosition.x;
    if (x <= 15) {
      return 'label-left-edge'; // Store on left edge, show label on right
    } else if (x >= 85) {
      return 'label-right-edge'; // Store on right edge, show label on right
    }
    return '';
  }

  // Hover event handlers
  onPointHover(point: ScatterDataPoint, event: MouseEvent): void {
    this.showYourStoreTooltip = false;
    this.hoveredPoint = point;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const result = this.calculateTooltipPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
    this.tooltipPosition = { x: result.x, y: result.y };
    this.isTooltipFlipped = result.isFlipped;
    this.tooltipArrowOffset = result.arrowOffset;
  }

  onPointLeave(): void {
    this.hoveredPoint = null;
    this.isTooltipFlipped = false;
  }

  onYourStoreHover(event: MouseEvent): void {
    this.hoveredPoint = null;
    this.showYourStoreTooltip = true;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const result = this.calculateTooltipPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
    this.yourStoreTooltipPosition = { x: result.x, y: result.y };
    this.isYourStoreTooltipFlipped = result.isFlipped;
    this.yourStoreTooltipArrowOffset = result.arrowOffset;
  }

  onYourStoreLeave(): void {
    this.showYourStoreTooltip = false;
    this.isYourStoreTooltipFlipped = false;
  }

  private calculateTooltipPosition(dotCenterX: number, dotCenterY: number): { x: number, y: number, isFlipped: boolean, arrowOffset: number } {
    const tooltipWidth = 220;
    const tooltipHeight = 200;
    const margin = 15;
    const headerHeight = 170; // Store header panel height (~74px top + ~95px panel height)
    const arrowHeight = 14; // Height of the tooltip arrow

    // Center tooltip horizontally on the exact dot center
    let x = dotCenterX - tooltipWidth / 2;

    // Ensure tooltip stays within viewport horizontally
    if (x < margin) {
      x = margin;
    } else if (x + tooltipWidth > window.innerWidth - margin) {
      x = window.innerWidth - tooltipWidth - margin;
    }

    // Arrow always points at the dot center X, compensating for any viewport clamping
    const arrowPx = Math.max(12, Math.min(tooltipWidth - 12, dotCenterX - x));
    const arrowOffset = (arrowPx / tooltipWidth) * 100;

    // Position tooltip above the dot center
    let y = dotCenterY - tooltipHeight - arrowHeight - margin;
    let isFlipped = false;

    const wouldBeHiddenByHeader = y < headerHeight;
    const hasSpaceBelow = dotCenterY + tooltipHeight + arrowHeight + margin < window.innerHeight;

    if (wouldBeHiddenByHeader && hasSpaceBelow) {
      y = dotCenterY + arrowHeight + margin;
      isFlipped = true;
    } else if (wouldBeHiddenByHeader && !hasSpaceBelow) {
      y = headerHeight + margin;
      isFlipped = false;
    } else if (y < margin) {
      y = dotCenterY + arrowHeight + margin;
      isFlipped = true;
    }

    if (y + tooltipHeight > window.innerHeight - margin) {
      y = window.innerHeight - tooltipHeight - margin;
    }

    return { x, y, isFlipped, arrowOffset };
  }

  // Click event handlers for tracking
  onPointClick(point: ScatterDataPoint, event: MouseEvent): void {
    console.log(event);
    // Find the full store ranking object for this point
    const storeRanking = this.rankings.find(r => r.store?.uuid === point.id);
    this.trackUserInteraction('scatter_point_click', {
      storeId: point.id,
      storeName: point.storeName,
      category: point.category,
      storeDetail: storeRanking?.store || null
    });
  }

  onYourStoreClick(event: MouseEvent): void {
    console.log("🚀 ~ StoreRankingScatterChartComponent ~ onYourStoreClick ~ event:", event)
    this.trackUserInteraction('your_store_click', {
      storeId: this.store?.uuid || this.currentStoreUuid,
      storeName: this.store?.name || this.yourStorePosition.name,
      category: 'current',
      storeDetail: this.store || null
    });
  }

  private trackUserInteraction(action: string, data: any): void {
    console.log("🚀 ~ StoreRankingScatterChartComponent ~ trackUserInteraction ~ action:", action)
    const metadata = {
      storeId: data.storeId,
      storeName: data.storeName,
      category: data.category,
    };
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
      metadata,
      USER_TRACKING_ACTIONS.RBS_RANK_PLOT_CLICK,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_RANK_PLOT_CLICK],
      true
    ).subscribe();
  }

  getTooltipStyle(): { [key: string]: string } {
    return {
      left: `${this.tooltipPosition.x}px`,
      top: `${this.tooltipPosition.y}px`
    };
  }

  getYourStoreTooltipStyle(): { [key: string]: string } {
    return {
      left: `${this.yourStoreTooltipPosition.x}px`,
      top: `${this.yourStoreTooltipPosition.y}px`
    };
  }

  getTooltipArrowStyle(): { [key: string]: string } {
    return {
      left: `${this.tooltipArrowOffset}%`,
      transform: 'translateX(-50%)'
    };
  }

  getYourStoreTooltipArrowStyle(): { [key: string]: string } {
    return {
      left: `${this.yourStoreTooltipArrowOffset}%`,
      transform: 'translateX(-50%)'
    };
  }

  private loadStoreData(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .pipe(filter((state) => !!state.store))
        .subscribe((state: DetailState) => {
          this.store = state.store;
          this.marketArea = state.marketArea;

          // Eagerly resolve status and fit display names for PDF export
          if (state.store) {
            const currentStatus = state.store.getCurrentStatus();
            if (currentStatus) {
              this.definitionService.findOneStoreStatusType(currentStatus.getStatusSystemName())
                .pipe(take(1))
                .subscribe(def => {
                  this.storeStatusDisplay = def?.displayName || currentStatus.getStatusDisplayName() || 'N/A';
                });
            } else {
              this.storeStatusDisplay = 'Unknown';
            }

            if (state.store.fit) {
              this.definitionService.findOneFit(state.store.fit as FitType)
                .pipe(take(1))
                .subscribe(def => {
                  this.fitCategoryDisplay = def?.displayName || String(state.store.fit);
                });
            }
          }

          // Trigger only once per store
          if (this.store && this.marketArea && this.store.uuid !== this._fetchedForStoreUuid) {
            this._fetchedForStoreUuid = this.store.uuid;
            this.fetchStoreRankings();
          }

          if (this.rankings?.length > 0) {
            this.processRankingsData();
          }
        })
    );

    this.addSubscription(
      selectvolumeDetailState(this.ngrxStore)
        .subscribe((volume) => {
          this.volume = volume;
          if (volume && volume.total) {
            this.estSalesVolume = `$${formatAsCurrency(volume.total, false)}`;
            this.estSalesSqft = volume.salesSqft
              ? `$${formatAsCurrency(volume.salesSqft)} /sqft`
              : 'Not Purchased';
          } else if (volume && !volume.total) {
            this.estSalesVolume = 'Not Purchased';
            this.estSalesSqft = 'Not Purchased';
          }
        })
    );
  }

  // Format value for display (2 decimal places to avoid ambiguity like $1.4M vs $1.4M)
  formatValue(value: number): string {
    if (isNaN(value)) return 'N/A';
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(0)}`;
  }

  // Format number with commas
  formatNumber(value: number): string {
    if (isNaN(value) || value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-US');
  }

  // Format fit type to readable format
  formatFitType(fitType: string): string {
    if (!fitType) return 'N/A';
    return fitType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  // Get percentile display
  getPercentileDisplay(percentile: number): string {
    if (!percentile) return '';
    const suffix = this.getOrdinalSuffix(percentile);
    return `${percentile}${suffix} percentile`;
  }

  private getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  private generateXAxisLabels(): void {
    const min = this.zoomMin ?? this.minValue;
    const max = this.zoomMax ?? this.maxValue;
    const range = max - min;

    // If no range (all stores have same value), create artificial range for labels
    let actualMin = min;
    let actualMax = max;
    let actualRange = range;

    if (range === 0) {
      // Create 20% padding around the single value, with reasonable minimum
      const padding = Math.max(Math.abs(min) * 0.2, 10);
      actualMin = min - padding;
      actualMax = max + padding;
      actualRange = actualMax - actualMin;
    }

    // Dynamic label count based on range and available space
    let labelCount;
    if (actualRange < 1000) {
      labelCount = 5;
    } else if (actualRange < 100000) {
      labelCount = 7;
    } else if (actualRange < 1000000) {
      labelCount = 8;
    } else {
      labelCount = 10;
    }

    const step = actualRange / (labelCount - 1);

    this.xAxisLabels = [];
    for (let i = 0; i < labelCount; i++) {
      this.xAxisLabels.push(this.formatValue(actualMin + step * i));
    }
  }

  /** Mutate positions in-place (no allocations) and rebuild visible list */
  private recalculatePositions(): void {
    const visibleRange = this.zoomMax - this.zoomMin || 1;
    const zMin = this.zoomMin;
    const zMax = this.zoomMax;
    const pts = this.dataPoints;

    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      p.x = Math.min(99, Math.max(1, ((p.rawValue - zMin) / visibleRange) * 100 + p.jitterX));
      p.visible = p.rawValue >= zMin && p.rawValue <= zMax;
    }

    if (this.yourStorePosition) {
      const visibleRange = this.zoomMax - this.zoomMin || 1;
      this.yourStorePosition.x = Math.min(99, Math.max(1,
        ((this.yourStorePosition.rawValue - this.zoomMin) / visibleRange) * 100
      ));
      this.isYourStoreVisible = this.yourStorePosition.rawValue >= this.zoomMin
        && this.yourStorePosition.rawValue <= this.zoomMax;
    }

    this.generateXAxisLabels();
    this.rebuildVisiblePoints();
    this.cdr.markForCheck();
  }

  /** Build the filtered array that the template actually renders */
  private rebuildVisiblePoints(): void {
    this.visibleDataPoints = this.dataPoints.filter(p => p.visible);
  }

  onSliderChange(context: ChangeContext): void {
    this.zoomMin = context.value;
    this.zoomMax = context.highValue;
    this.isZoomActive = this.zoomMin > this.minValue || this.zoomMax < this.maxValue;
    this.recalculatePositions();
    this.syncInputDisplays();
  }

  /** When input gains focus, show the raw number so user can easily edit */
  onZoomInputFocus(side: 'min' | 'max', inputEl: HTMLInputElement): void {
    const val = side === 'min' ? this.zoomMin : this.zoomMax;
    inputEl.value = String(Math.round(val));
    inputEl.select();
  }

  /** Live preview as user types — debounced to avoid lag on large datasets */
  onZoomInputChange(side: 'min' | 'max', inputEl: HTMLInputElement): void {
    clearTimeout(this.zoomInputDebounceTimer);
    this.zoomInputDebounceTimer = setTimeout(() => {
      const parsed = this.parseInputValue(inputEl.value);
      if (isNaN(parsed)) return;
      this.applyZoomValue(side, parsed);
    }, 150);
  }

  allowOnlyNumbers(event: KeyboardEvent) {
  const charCode = event.key.charCodeAt(0);

  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
}

  /** On blur, validate, apply, reformat — always restore a valid display */
  onZoomInputCommit(side: 'min' | 'max', inputEl: HTMLInputElement): void {
    clearTimeout(this.zoomInputDebounceTimer);
    const parsed = this.parseInputValue(inputEl.value);
    if (!isNaN(parsed)) {
      this.applyZoomValue(side, parsed);
    }
    // Always sync display back to current valid values (reverts invalid/out-of-range input)
    this.syncInputDisplays();
  }

  resetZoom(): void {
    this.zoomMin = this.minValue;
    this.zoomMax = this.maxValue;
    this.isZoomActive = false;
    this.isYourStoreVisible = true;
    this.recalculatePositions();
    this.syncInputDisplays();
  }

  private parseInputValue(raw: string): number {
    // Strip everything except digits and decimal point
    const cleaned = raw.replace(/[^0-9.]/g, '');
    return cleaned.length > 0 ? parseFloat(cleaned) : NaN;
  }

  private applyZoomValue(side: 'min' | 'max', value: number): void {
    if (side === 'min') {
      // Min cannot go below data floor or above current max
      if (value >= this.zoomMax) {
        this.flashInputError(this.minInputRef);
        return;
      }
      this.zoomMin = Math.max(this.minValue, value);
    } else {
      // Max cannot go above data ceil or below current min
      if (value <= this.zoomMin) {
        this.flashInputError(this.maxInputRef);
        return;
      }
      this.zoomMax = Math.min(this.maxValue, value);
    }
    this.isZoomActive = this.zoomMin > this.minValue || this.zoomMax < this.maxValue;
    this.recalculatePositions();
  }

  /** Briefly flash input border red to indicate invalid value */
  private flashInputError(ref: ElementRef<HTMLInputElement>): void {
    if (!ref?.nativeElement) return;
    const el = ref.nativeElement;
    el.classList.add('zoom-input-error');
    setTimeout(() => el.classList.remove('zoom-input-error'), 800);
  }

  /** Format value for display inside input (number with commas) */
  formatInputDisplay(value: number): string {
    if (isNaN(value)) return '0';
    return Math.round(value).toLocaleString('en-US');
  }

  /** Push current zoomMin/zoomMax into the input DOM elements directly */
  private syncInputDisplays(): void {
    if (this.minInputRef?.nativeElement) {
      this.minInputRef.nativeElement.value = this.formatInputDisplay(this.zoomMin);
    }
    if (this.maxInputRef?.nativeElement) {
      this.maxInputRef.nativeElement.value = this.formatInputDisplay(this.zoomMax);
    }
  }

  private initSliderOptions(): void {
    const range = this.maxValue - this.minValue || 1;
    // Cap at 500 steps — beyond that the slider freezes the browser.
    // Input boxes provide exact values so the slider only needs coarse navigation.
    const step = Math.max(1, Math.ceil(range / 500));
    this.sliderOptions = {
      floor: Math.floor(this.minValue),
      ceil: Math.ceil(this.maxValue),
      step,
      noSwitching: true,
      hideLimitLabels: true,
      hidePointerLabels: true,
    };
  }

  private fetchStoreRankings(): void {
    this.rankingsLoading = true;
    this.hasData = false;
    this.cdr.markForCheck();
    this._fetchTrigger$.next();
  }

  private buildRankingObservable(): Observable<StoreRanking[]> {
    return selectDetailState(this.ngrxStore).pipe(
      filter(state => !!state.store && !!state.marketArea),
      take(1),
      switchMap(detailState =>
        selectUserState(this.ngrxStore).pipe(
          take(1),
          switchMap(userState => {
            const userPreferences = userState?.userPreferences;
            const currentFilters = this.filters || this.defaultFilters;

            let regionType: RegionType;
            switch (currentFilters.driveTime) {
              case 'Ring': regionType = RegionType.RING; break;
              case 'Market Area': regionType = RegionType.MARKET_AREA; break;
              case 'Trade Area': regionType = RegionType.TRADE_AREA; break;
              default: regionType = RegionType.DRIVE_TIME;
            }

            const comparisonRequest = new StoreComparison({
              store: detailState.store,
              volume: detailState.volume,
              comparisonType: currentFilters.comparisonType,
              keyIndicator: this.keyIndicator,
              region: regionType,
              driveTimeMinutes: regionType === RegionType.DRIVE_TIME ? currentFilters.number : undefined,
              ringMiles: regionType === RegionType.RING ? currentFilters.number : undefined,
              salesSqftDisplayType: userPreferences?.salesSqftDisplayMode || SalesSqftDisplayType.TOTAL_AREA,
              salesVolumeDisplayType: userPreferences?.salesVolumeDisplayMode || SalesVolumeDisplayType.WEEKLY
            });

            return this.storeComparisonService.appendGeoJson(comparisonRequest).pipe(
              switchMap((requestWithGeoJson: StoreComparison) => {
                const rankingRequest = new StoreRankingRequest({
                  geoJson: requestWithGeoJson.geoJson,
                  comparisonType: currentFilters.comparisonType,
                  keyIndicator: this.keyIndicator,
                  salesSqftDisplayType: userPreferences?.salesSqftDisplayMode || SalesSqftDisplayType.TOTAL_AREA,
                  salesVolumeDisplayType: userPreferences?.salesVolumeDisplayMode || SalesVolumeDisplayType.WEEKLY,
                  bannerUuid: currentFilters.comparisonType === ComparisonType.BANNER ? detailState.store.banner?.uuid : undefined,
                  fitCategory: currentFilters.comparisonType === ComparisonType.FIT_CATEGORY ? detailState.store.fit : undefined
                });
                return this.storeRankingService.findRankByStore(detailState.store.uuid, rankingRequest);
              }),
              catchError(err => {
                console.error('rank-by-store API ERROR:', err);
                return of([] as StoreRanking[]);
              })
            );
          })
        )
      )
    );
  }

  async downloadScatterData(): Promise<void> {
    if (!this.rankings || this.rankings.length === 0) return;

    this.pdfGenerating = true;
    this.cdr.markForCheck();

    try {
    // Track download action
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
      { storeId: this.store?.uuid, storeName: this.store?.name },
      USER_TRACKING_ACTIONS.RBS_RANKING_DATA_EXPORTED,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_RANKING_DATA_EXPORTED],
      true
    ).subscribe();

    // ── Store Address (same as comprehensive ranking table exportToPdf) ──
    const loc = this.store?.space?.location;
    const storeAddress = loc
      ? [loc.addressLine1, loc.city, loc.state, loc.postalCode].filter(Boolean).join(', ')
      : (this.store?.name || 'N/A');

    // ── Status (same as comprehensive ranking table) ──
    const storeStatusDisplay = this.storeStatusDisplay;

    // ── FIT Category (same as comprehensive ranking table) ──
    const fitCategoryDisplay = this.fitCategoryDisplay;

    // ── Sales Volume (same as comprehensive ranking table) ──
    const estSalesVolume = this.estSalesVolume;
    const estSalesSqft = this.estSalesSqft;

    // Build PDF data rows
    const pdfRows = await Promise.all(this.rankings.map(async ranking => {
      let fitDisplay = 'N/A';
      if (ranking.store?.fit) {
        const fitDef = await this.definitionService.findOneFit(ranking.store.fit as FitType).toPromise();
        fitDisplay = fitDef?.displayName || ranking.store.fit;
      }

      return {
        rank: ranking.rank || 0,
        locationName: ranking.store?.name || 'Unknown',
        fit: fitDisplay,
        status: ranking.store?.currentStatus?.type?.displayName || ranking.store?.currentStatus?.type?.systemName || 'N/A',
        salesPerSqft: ranking.volume?.salesSqftByTotalArea
          ? `$${this.formatNumber(ranking.volume.salesSqftByTotalArea)}`
          : 'N/A',
        totalArea: ranking.volume?.salesArea
          ? `${this.formatNumber(ranking.volume.salesArea)} sq ft`
          : 'N/A',
        totalSalesVolume: ranking.volume?.total
          ? `$${this.formatNumber(ranking.volume.total)}`
          : 'N/A',
        isSelected: ranking.store?.uuid === this.store?.uuid
      };
    }));

    // Sort by rank
    pdfRows.sort((a, b) => a.rank - b.rank);

    // Get current filters
    const currentFilters = this.filters || this.defaultFilters;

    // Build region label
    let regionLabel = '';
    switch (currentFilters.driveTime) {
      case 'Ring':
        regionLabel = 'Ring';
        break;
      case 'Market Area':
        regionLabel = 'Market Area';
        break;
      case 'Trade Area':
        regionLabel = 'Trade Area';
        break;
      default:
        regionLabel = 'Drive Time';
    }

    // Build miles/minutes label based on region type
    let milesLabel = '';
    let minutesLabel = '';
    if (currentFilters.driveTime === 'Ring') {
      milesLabel = String(currentFilters.number || '');
    } else if (currentFilters.driveTime === 'Drive time') {
      minutesLabel = String(currentFilters.number || '');
    }

    await this.dynamicPdfService.generateScatterChartPdf(
      {
        storeName: this.store?.name || this.yourStorePosition?.name || 'Store',
        storeAddress,
        storeStatus: storeStatusDisplay,
        estSalesVolume,
        estSalesSqft,
        fitCategory: fitCategoryDisplay,
        chartTitle: `${this.store?.name || this.yourStorePosition?.name || 'Store'} (${this.getXAxisTitle()})`,
        keyIndicator: this.getXAxisTitle(),
        comparisonType: this.getComparisonTypeLabel(),
        filters: {
          region: regionLabel,
          miles: milesLabel || undefined,
          minutes: minutesLabel || undefined,
          storeType: currentFilters.storeType || 'All Grocery Stores'
        },
        tableRows: pdfRows
      },
      `scatter-chart-${Date.now()}.pdf`
    );
    } finally {
      this.pdfGenerating = false;
      this.cdr.markForCheck();
    }
  }

  private getComparisonTypeLabel(): string {
    switch (this.comparisonType) {
      case ComparisonType.ALL:
        return 'All Stores';
      case ComparisonType.BANNER:
        return 'Same Banner';
      case ComparisonType.FIT_CATEGORY:
        return 'Same Fit Category';
      default:
        return 'All Stores';
    }
  }
}
